import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, OnDestroy, output, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { ApiError, FileCategory, MediaSortMode, ProjectFile, VariantFile } from '../../models';
import { EditorFilesService, MediaReferenceReport } from '../../../features/editor/services/editor-files.service';
import { EditorJobsService } from '../../../features/editor/services/editor-jobs.service';
import { EditorStateService } from '../../../features/editor/services/editor-state.service';
import { UploadService } from '../../../core/services/upload.service';
import { DuplicateResolutionEntry, UploadItem } from '../../../core/services/upload.types';
import { acceptForCategory, validateFileForCategory } from '../../utils/file-category';
import { UploadDuplicateDialogComponent, type DuplicateResolution, type DuplicateRow } from '../upload-duplicate-dialog/upload-duplicate-dialog';

/** Union type for grid/list rows — merges backend-confirmed files with in-progress uploads. */
type DisplayedItem = ProjectFile | UploadItem;

const CATEGORY_LABELS: Record<FileCategory, string> = {
	'video': 'Video',
	'mask': 'Mask',
	'video-mask': 'Video Mask',
	'images': 'Images',
	'fonts': 'Fonts',
	'audios': 'Audios',
	'subtitles': 'Subtitles',
};

const CATEGORY_ICONS: Record<FileCategory, string> = {
	'video': 'pi pi-video',
	'mask': 'pi pi-eye',
	'video-mask': 'pi pi-eye-slash',
	'images': 'pi pi-image',
	'fonts': 'pi pi-palette',
	'audios': 'pi pi-volume-up',
	'subtitles': 'pi pi-language',
};

const CATEGORY_ORDER: FileCategory[] = ['video', 'mask', 'images', 'fonts', 'audios', 'subtitles'];

/** Minimum time the "Loading files..." state stays visible once shown, so a
 * fast response doesn't read as a flicker. */
const MIN_LOADING_DISPLAY_MS = 300;

/** Distance (px) from the scroll container's top/bottom edge that triggers
 * auto-scroll during a reorder drag (DEV-251). */
const AUTO_SCROLL_EDGE_PX = 48;
/** Scroll speed (px per animation frame) once inside the edge zone. */
const AUTO_SCROLL_SPEED_PX = 12;

@Component({
	selector: 'app-file-browser',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [Dialog, FormsModule, InputText, UploadDuplicateDialogComponent],
	templateUrl: './file-browser.html',
	styleUrl: './file-browser.scss',
})
export class FileBrowserComponent implements OnDestroy {
	groupedFiles = input<Record<FileCategory, ProjectFile[]>>({
		'video': [],
		'mask': [],
		'video-mask': [],
		'images': [],
		'fonts': [],
		'audios': [],
		'subtitles': [],
	});
	loading = input(false);
	projectSlug = input('');

	/** A variant group's variant list changed (upload finished, variant deleted).
	 * `namesMap` in project.json is a snapshot taken when the group was first
	 * wired into a scene or track, so without this it silently drifts from the
	 * group it is supposed to describe. */
	variantGroupChanged = output<string>();

	/** A media delete removed timeline references (deleted referencing scenes /
	 * dropped audio-track entries), so the editor must reload the project to drop
	 * the now-gone scenes/tracks from the timeline. */
	timelineChanged = output<void>();
	/** A media was deleted (and its refs scrubbed server-side); carries the deleted
	 * media id so the editor can drop just that media's src from the open form. */
	mediaDeleted = output<string>();

	private filesService = inject(EditorFilesService);
	private jobsService = inject(EditorJobsService);
	private uploadService = inject(UploadService);
	private stateService = inject(EditorStateService);
	private messages = inject(MessageService);
	private destroyRef = inject(DestroyRef);

	/** Mirrors `EditorStateService.isReadOnly()` — drives every write-action
	 * visibility / disabled state in the file-browser template. */
	protected readonly isReadOnly = this.stateService.isReadOnly;

	/** Upload ids already pushed into filesService. Prevents double-add on
	 * re-runs of the effect below. Memory: a handful of UUIDs per session. */
	private readonly addedUploadIds = new Set<string>();

	/** Debounced view of `loading()` — floored to MIN_LOADING_DISPLAY_MS once
	 * shown so a fast load doesn't flash the "Loading files..." state. Starts
	 * `true`: `EditorFilesService.loading` defaults to `false` until its first
	 * `load()` call (which only fires after project.json resolves), so without
	 * this, categories briefly render with their real (empty) "no files yet"
	 * state before the request even starts — now that categories default to
	 * expanded (PRO-191), that flash is visible instead of hidden behind a
	 * collapsed section. */
	protected showLoading = signal(true);
	private loadingDisplayed = true;
	private loadingShownAt = 0;
	private hasSeenLoadStart = false;

	constructor() {
		effect((onCleanup) => {
			const isLoading = this.loading();
			if (isLoading) {
				this.hasSeenLoadStart = true;
				this.loadingShownAt = Date.now();
				this.loadingDisplayed = true;
				this.showLoading.set(true);
				return;
			}
			if (!this.hasSeenLoadStart) return;
			if (!this.loadingDisplayed) return;
			const remaining = MIN_LOADING_DISPLAY_MS - (Date.now() - this.loadingShownAt);
			if (remaining <= 0) {
				this.loadingDisplayed = false;
				this.showLoading.set(false);
				return;
			}
			const handle = setTimeout(() => {
				this.loadingDisplayed = false;
				this.showLoading.set(false);
			}, remaining);
			onCleanup(() => clearTimeout(handle));
		});

		// Scan the upload queue on any items() / status change and push any
		// done-but-not-yet-added uploads into the local files signal. Idempotent
		// via `addedUploadIds`. Handles batch completions correctly — an earlier
		// signal-based design would drop completions that fired in the same
		// microtask (classic signal-as-event bug).
		effect(() => {
			const items = this.uploadService.items();
			const slug = this.projectSlug();
			const completedGroupIds = new Set<string>();
			let variantGroupCompleted = false;
			let regularFileCompleted = false;
			for (const item of items) {
				if (item.projectSlug !== slug) continue;
				if (item.status() !== 'done') continue;
				if (this.addedUploadIds.has(item.id)) continue;
				// Variant uploads don't become their own file row — they attach to
				// the existing group. Still mark as processed (so we don't fire the
				// reload repeatedly) and trigger a refresh so the group's variant
				// list picks up the new entries.
				if (item.groupId) {
					this.addedUploadIds.add(item.id);
					completedGroupIds.add(item.groupId);
					variantGroupCompleted = true;
					continue;
				}
				if (!item.response) continue;
				this.addedUploadIds.add(item.id);
				this.filesService.addLocalFile(item.response, item.id, item.duplicateWarning);
				regularFileCompleted = true;
				this.maybeRegenerateAfterReplace(item);
			}
			// `addLocalFile` only appends — it doesn't know about the category's
			// sort mode, so an alphabetically-sorted category would otherwise show
			// the new file pinned at the top forever. Re-fetch from the backend so
			// it lands in the correct (natural-sort or persisted custom) position.
			if (variantGroupCompleted) {
				// Emit after the reload, not before: the parent reads the
				// group's variant list back out of `filesService`.
				this.filesService.reloadAndThen(() => {
					this.refreshOpenVariantDialog();
					for (const groupId of completedGroupIds) this.variantGroupChanged.emit(groupId);
				});
			} else if (regularFileCompleted) {
				this.filesService.reloadAndThen();
			}
		});
	}

	/**
	 * PRO-220: a replace-in-place upload wipes the media's generated versions
	 * server-side (`handleReplaceUpload`) but keeps the same `_id`/`baseUrl`, so
	 * no scene/track `src` value actually changes — the drop-time auto-generate
	 * trigger (scene-form.ts / autoGenerateForMedia) never fires for it. Left
	 * alone, a replaced media that's already wired into the timeline sits
	 * ungenerated forever: a video/mask stays un-transcoded (hatched block, and
	 * any previously-built video-mask composite goes stale), a replaced audio
	 * keeps only its `.mp3` while the player always fetches the `.aac` sibling →
	 * 404 → "Unable to decode audio data". Only regenerate when the media is
	 * actually referenced on the timeline — an unused replace sitting in the pool
	 * shouldn't burn ffmpeg time. That "is it used?" answer comes from the backend
	 * (`response.usedOnTimeline`): it's project-wide, whereas the editor only knows
	 * the sources of scenes currently loaded in its session. Running through
	 * generateVersions + andTrack (not a silent server-side queue) is what surfaces
	 * the active-job chip, the completion toast, and the media-state refresh that
	 * clears the hatching. Once a video/mask versions job completes,
	 * `EditorJobsService.maybeAutoComposite` picks up the composite rebuild on its
	 * own, same as the normal upload path.
	 */
	private maybeRegenerateAfterReplace(item: UploadItem): void {
		if (!item.replaceMediaId || !item.response) return;
		const { category, name, usedOnTimeline } = item.response;
		// Only these categories have generated codec/resolution siblings to rebuild.
		const generatable = category === 'video' || category === 'mask'
			|| category === 'images' || category === 'audios';
		if (!generatable || !usedOnTimeline) return;

		this.filesService.generateVersions(item.replaceMediaId)
			.pipe(this.jobsService.andTrack({
				mediaId: item.replaceMediaId,
				mediaName: name,
				mediaCategory: category,
				destroyRef: this.destroyRef,
			}))
			.subscribe();
	}

	/** Sync the currently-open variant dialog with the freshly-reloaded files
	 * signal so newly-uploaded variants appear in the dialog without closing it. */
	private refreshOpenVariantDialog(): void {
		const current = this.variantDialogFile();
		if (!current?.id) return;
		const updated = this.filesService.files().find(f => f.id === current.id);
		if (updated) this.variantDialogFile.set(updated);
	}

	protected collapsed = signal<Set<FileCategory>>(new Set());
	protected categoryViewMode = signal<Map<FileCategory, 'grid' | 'list'>>(new Map());

	protected getCatView(cat: FileCategory): 'grid' | 'list' {
		return this.categoryViewMode().get(cat) ?? 'grid';
	}

	protected toggleCatView(cat: FileCategory, mode: 'grid' | 'list'): void {
		this.categoryViewMode.update(m => {
			const next = new Map(m);
			next.set(cat, mode);
			return next;
		});
	}

	// ── Sort mode + drag-to-reorder ─────────────────────────────────────────

	protected getSortMode(cat: FileCategory): MediaSortMode {
		return this.filesService.sortModes()[cat] ?? 'alphabetical';
	}

	protected setSortMode(cat: FileCategory, mode: MediaSortMode): void {
		if (this.getSortMode(cat) === mode) return;
		this.filesService.setSortMode(cat, mode);
	}

	/** Drag-to-reorder is always available — same as the existing drag-to-timeline
	 * gesture for video/audio, it doesn't require switching modes first. Dropping
	 * implicitly switches the category to 'custom' (see `EditorFilesService.reorderCategory`). */
	protected canReorder(): boolean {
		return !this.isReadOnly();
	}

	/** File currently being dragged for in-panel reorder (distinct from the
	 * drag-to-timeline payload set in `onDragStart`, which uses the native
	 * DataTransfer instead — dragover/drop can't read DataTransfer contents
	 * until the drop fires, so the dragged id is tracked here). */
	private reorderDragId = signal<string | null>(null);
	protected dragOverFileId = signal<string | null>(null);

	protected onReorderDragStart(event: DragEvent, file: ProjectFile): void {
		if (!file.id) return;
		this.reorderDragId.set(file.id);
		event.dataTransfer?.setData('text/plain', file.id);
	}

	protected onReorderDragOver(event: DragEvent, file: ProjectFile): void {
		if (!this.reorderDragId() || file.id === this.reorderDragId()) return;
		event.preventDefault();
		this.dragOverFileId.set(file.id ?? null);
		this.updateAutoScroll(event);
	}

	protected onReorderDragLeave(file: ProjectFile): void {
		if (this.dragOverFileId() === file.id) this.dragOverFileId.set(null);
	}

	/** Fires on the dragged element once the drag operation ends, however it
	 * ends (successful drop, dropped outside a valid target, Esc) — the one
	 * reliable place to stop auto-scroll, unlike per-item `dragleave` which
	 * fires constantly while moving between adjacent items. */
	protected onReorderDragEnd(): void {
		this.stopAutoScroll();
	}

	protected onReorderDrop(event: DragEvent, targetFile: ProjectFile, cat: FileCategory): void {
		event.preventDefault();
		this.stopAutoScroll();
		const draggedId = this.reorderDragId();
		this.reorderDragId.set(null);
		this.dragOverFileId.set(null);
		if (!draggedId || !targetFile.id || draggedId === targetFile.id) return;

		const current = this.groupedFiles()[cat] ?? [];
		const ids = current.map(f => f.id).filter((id): id is string => !!id);
		const fromIdx = ids.indexOf(draggedId);
		const toIdx = ids.indexOf(targetFile.id);
		if (fromIdx === -1 || toIdx === -1) return;

		const next = ids.slice();
		next.splice(fromIdx, 1);
		next.splice(toIdx, 0, draggedId);
		this.filesService.reorderCategory(cat, next);
	}

	// ── Auto-scroll during reorder drag (DEV-251) ───────────────────────────
	// Native HTML5 drag-and-drop (used here because this grid layout can't use
	// CDK drag-drop, see the Media pool sorting note in spec/plans/index.md)
	// has no built-in edge-auto-scroll, unlike CDK. Scrolls the nearest
	// scrollable ancestor while the pointer sits near its top/bottom edge.

	private autoScrollContainer: HTMLElement | null = null;
	private autoScrollDirection = 0;
	private autoScrollRafId: number | null = null;

	private findScrollableAncestor(el: HTMLElement | null): HTMLElement | null {
		let node = el?.parentElement ?? null;
		while (node) {
			const style = getComputedStyle(node);
			if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
				return node;
			}
			node = node.parentElement;
		}
		return null;
	}

	private updateAutoScroll(event: DragEvent): void {
		const container = this.autoScrollContainer ?? this.findScrollableAncestor(event.currentTarget as HTMLElement);
		if (!container) return;
		this.autoScrollContainer = container;

		const rect = container.getBoundingClientRect();
		if (event.clientY < rect.top + AUTO_SCROLL_EDGE_PX) {
			this.autoScrollDirection = -1;
		} else if (event.clientY > rect.bottom - AUTO_SCROLL_EDGE_PX) {
			this.autoScrollDirection = 1;
		} else {
			this.autoScrollDirection = 0;
		}

		if (this.autoScrollDirection !== 0 && this.autoScrollRafId === null) {
			const step = (): void => {
				if (this.autoScrollDirection === 0 || !this.autoScrollContainer) {
					this.autoScrollRafId = null;
					return;
				}
				this.autoScrollContainer.scrollTop += this.autoScrollDirection * AUTO_SCROLL_SPEED_PX;
				this.autoScrollRafId = requestAnimationFrame(step);
			};
			this.autoScrollRafId = requestAnimationFrame(step);
		}
	}

	private stopAutoScroll(): void {
		if (this.autoScrollRafId !== null) {
			cancelAnimationFrame(this.autoScrollRafId);
			this.autoScrollRafId = null;
		}
		this.autoScrollContainer = null;
		this.autoScrollDirection = 0;
	}

	ngOnDestroy(): void {
		this.stopAutoScroll();
	}
	protected hoveredFile = signal<ProjectFile | null>(null);
	protected variantDialogFile = signal<ProjectFile | null>(null);
	protected variantDialogVisible = signal(false);
	protected variantFilter = signal('');

	/** True while any variant upload is in flight for the currently-open dialog
	 * group. Drives the dialog's "Upload variants" button spinner + disabled
	 * state. Derived from UploadService since variant uploads now flow through
	 * the upload queue. */
	protected uploadingVariants = computed(() => {
		const groupId = this.variantDialogFile()?.id;
		if (!groupId) return false;
		return this.uploadService.items().some(i =>
			i.groupId === groupId
			&& (i.status() === 'queued' || i.status() === 'probing' || i.status() === 'uploading'),
		);
	});

	/**
	 * Per-category upload indicator — scoped to this browser's project + the
	 * given category. Replaces the previous global `uploading` signal so
	 * uploading into one category doesn't show spinners on the others.
	 *
	 * Returns a `computed()` so each category's count is independently reactive
	 * (no signal reads leak across categories).
	 */
	protected uploadingCountFor = (cat: FileCategory) => computed(() =>
		this.uploadService.items().filter(i =>
			i.projectSlug === this.projectSlug()
			&& i.category === cat
			&& (i.status() === 'queued' || i.status() === 'probing' || i.status() === 'uploading'),
		).length,
	);

	protected hasActiveUploadsFor = (cat: FileCategory) => computed(() =>
		this.uploadingCountFor(cat)() > 0,
	);

	/**
	 * Type guard — distinguishes pending-upload rows (UploadItem) from
	 * backend-confirmed ProjectFile rows. UploadItem carries a `status` signal;
	 * ProjectFile carries `baseUrl` + `name` strings.
	 */
	protected isPendingUpload(row: DisplayedItem): row is UploadItem {
		return 'status' in row && typeof (row as UploadItem).status === 'function';
	}

	/** Cast helper for narrow-after-guard template usage. */
	protected asProjectFile(row: DisplayedItem): ProjectFile {
		return row as ProjectFile;
	}

	/** Cancel a pending upload from inside the file browser grid. Terminal
	 * items (validation errors) route through dismissItem so the ✕ button
	 * actually removes the row. */
	protected cancelPendingUpload(item: UploadItem, event: MouseEvent): void {
		event.preventDefault();
		event.stopPropagation();
		const status = item.status();
		if (status === 'error' || status === 'cancelled' || status === 'done') {
			this.uploadService.dismissItem(item.id);
		} else {
			this.uploadService.cancel(item.id);
		}
	}

	/** Retry a pending upload that errored out. */
	protected retryPendingUpload(item: UploadItem, event: MouseEvent): void {
		event.preventDefault();
		event.stopPropagation();
		this.uploadService.retry(item.id);
	}

	/**
	 * Unified track key for the merged grid. A pending UploadItem and its
	 * confirmed ProjectFile successor share the same key (the upload item's id
	 * via `correlationId`) so Angular reuses the same `@for` DOM node across
	 * the placeholder → confirmed swap — only the inner `@if` template flips.
	 */
	protected trackDisplayed = (_idx: number, row: DisplayedItem): string => {
		if (this.isPendingUpload(row)) return `slot:${row.id}`;
		if (row.correlationId) return `slot:${row.correlationId}`;
		return `file:${row.baseUrl}`;
	};

	protected filteredVariants = computed(() => {
		const file = this.variantDialogFile();
		if (!file?.variants) return [];
		const filter = this.variantFilter().toLowerCase();
		if (!filter) return file.variants;
		return file.variants.filter(v => v.name.toLowerCase().includes(filter));
	});

	protected categories = computed(() => {
		const grouped = this.groupedFiles();
		const slug = this.projectSlug();
		const allItems = this.uploadService.items();
		return CATEGORY_ORDER
			.map(cat => {
				const confirmed: ProjectFile[] = grouped[cat] ?? [];
				// Still-active upload items for this project + category, in enqueue
				// order. Excluded from the reserved top slot:
				// - `done` — once confirmed, the file already lives in `confirmed`
				//   (pushed there by `addLocalFile`, then re-sorted into place by the
				//   completion effect's follow-up `reloadAndThen()`). Keeping it in
				//   the reserved slot here too would both duplicate it AND override
				//   the freshly-fetched sort order by re-pinning it to the top for as
				//   long as it lingers in the upload queue (up to the panel's ~10s
				//   auto-dismiss) — which is exactly the bug this exclusion fixes.
				// - Replace-in-place items (`replaceMediaId` set) — they target an
				//   existing row, progress surfaces in the upload panel and the
				//   existing row updates in place once the upload completes.
				// - Variant-group uploads (`groupId` set) — they attach to an
				//   existing variant group, never produce a top-level row, and so
				//   never get absorbed by `correlationId === item.id`. Without
				//   this exclusion they linger as ghost placeholders forever
				//   (cleared only by a full reload that wipes uploadService.items()).
				//   The variant dialog refreshes via the completion effect.
				const uploads: UploadItem[] = allItems.filter(i =>
					i.projectSlug === slug
					&& i.category === cat
					&& i.status() !== 'cancelled'
					&& i.status() !== 'done'
					&& !i.replaceMediaId
					&& !i.groupId,
				);

				// Still-uploading items reserve a slot at the top, in enqueue order,
				// so their placeholder doesn't jump around mid-progress. Confirmed
				// files (including ones just absorbed from a completed upload) render
				// in whatever order `confirmed` already has — the backend's sort
				// order after the completion-triggered reload.
				const files: DisplayedItem[] = [...uploads, ...confirmed];

				const activeCount = allItems.filter(i =>
					i.projectSlug === slug
					&& i.category === cat
					&& (i.status() === 'queued' || i.status() === 'probing' || i.status() === 'uploading'),
				).length;
				return {
					key: cat,
					label: CATEGORY_LABELS[cat],
					icon: CATEGORY_ICONS[cat],
					files,
					count: confirmed.length,
					activeCount,
				};
			});
	});

	protected isCollapsed(cat: FileCategory): boolean {
		return this.collapsed().has(cat);
	}

	protected toggleCategory(cat: FileCategory): void {
		this.collapsed.update(set => {
			const next = new Set(set);
			if (next.has(cat)) next.delete(cat);
			else next.add(cat);
			return next;
		});
	}

	protected isVideoCategory(file: ProjectFile): boolean {
		return file.category === 'video' || file.category === 'mask' || file.category === 'video-mask';
	}

	protected isImageCategory(file: ProjectFile): boolean {
		return file.category === 'images';
	}

	protected isHovered(file: ProjectFile): boolean {
		return this.hoveredFile() === file;
	}

	protected stripExt(name: string): string {
		const dotIdx = name.lastIndexOf('.');
		return dotIdx > 0 ? name.substring(0, dotIdx) : name;
	}

	/** Case-insensitive basename (no extension) used as the duplicate-detection
	 * key. Matches `foo.mp4` ↔ `FOO.MOV` so cross-extension re-uploads still
	 * surface the dialog (where the Replace radio gets disabled by the extension
	 * mismatch check). */
	private basenameKey(name: string): string {
		return name.replace(/\.[^/.]+$/, '').toLowerCase();
	}

	protected getCategoryIcon(cat: FileCategory): string {
		return CATEGORY_ICONS[cat];
	}

	protected openVariantDialog(file: ProjectFile, event: MouseEvent): void {
		event.preventDefault();
		event.stopPropagation();
		this.variantFilter.set('');
		this.variantDialogFile.set(file);
		this.variantDialogVisible.set(true);
	}

	protected closeVariantDialog(): void {
		this.variantDialogVisible.set(false);
		this.variantDialogFile.set(null);
	}

	/** Dialog state for the per-batch duplicate-resolution prompt. The dialog
	 * stays open until the user confirms (resolutions emitted) or cancels
	 * (no enqueue). Holding the file list + category here decouples the
	 * picker handler from the dialog's lifecycle. */
	protected duplicateDialogVisible = signal(false);
	protected duplicateDialogRows = signal<DuplicateRow[]>([]);
	protected duplicateDialogNonDuplicates = signal<File[]>([]);
	private duplicateDialogCategory: FileCategory | null = null;

	protected onUploadFileSelected(event: Event, category: FileCategory): void {
		const input = event.target as HTMLInputElement;
		const files = input.files;
		if (files?.length) this.processIncomingFiles(Array.from(files), category);
		input.value = '';
	}

	/**
	 * Shared entry point for both the file-input picker and OS drag-and-drop
	 * (dragged from Finder/Explorer onto a category section): resolves name
	 * collisions against the loaded media list and either enqueues directly or
	 * opens the duplicate-resolution dialog.
	 *
	 * Non-variant-group entries only — variant groups are out of scope for the
	 * replace flow (their additive upload uses a separate route).
	 *
	 * Key by case-insensitive basename (without extension) so cross-extension
	 * re-uploads (e.g. `foo.mov` while existing is `foo.mp4`) collide and
	 * surface the dialog. The dialog disables the Replace radio whenever the
	 * extensions differ — see upload-duplicate-dialog `extensionMatches`.
	 */
	private processIncomingFiles(fileList: File[], category: FileCategory): void {
		const slug = this.projectSlug();
		if (!slug || fileList.length === 0) return;

		const existing = (this.groupedFiles()[category] ?? []).filter(f => !f.variants);
		const existingByBasename = new Map<string, ProjectFile>();
		for (const media of existing) {
			existingByBasename.set(this.basenameKey(media.name), media);
		}

		const duplicates: DuplicateRow[] = [];
		const nonDuplicates: File[] = [];
		for (const file of fileList) {
			const match = existingByBasename.get(this.basenameKey(file.name));
			if (match) duplicates.push({ file, existing: match });
			else nonDuplicates.push(file);
		}

		if (duplicates.length === 0) {
			this.uploadService.enqueue(fileList, slug, category);
			return;
		}

		this.duplicateDialogCategory = category;
		this.duplicateDialogRows.set(duplicates);
		this.duplicateDialogNonDuplicates.set(nonDuplicates);
		this.duplicateDialogVisible.set(true);
	}

	// ── OS drag-and-drop upload (DEV-228) ───────────────────────────────────
	// Distinguished from the in-app drag-to-reorder (above) and drag-to-timeline
	// (`onDragStart`) gestures by checking `dataTransfer.types` for the native
	// `'Files'` type, which is only present for a drag originating outside the
	// browser (e.g. Finder/Explorer). In-app drags carry `'text/plain'` or
	// `'application/webout-file'` instead, so this never intercepts them.

	protected dragOverCategory = signal<FileCategory | null>(null);

	/** Per-category dragenter/dragleave depth — these events fire once per
	 * descendant element entered/left, not just once for the container, so a
	 * naive set-on-enter/clear-on-leave would flicker the highlight while the
	 * pointer moves over child elements. */
	private osDragDepth = new Map<FileCategory, number>();

	private isOsFileDrag(event: DragEvent): boolean {
		return !!event.dataTransfer?.types.includes('Files');
	}

	protected onOsDragEnter(event: DragEvent, cat: FileCategory): void {
		if (!this.isUploadableCategory(cat) || !this.isOsFileDrag(event)) return;
		event.preventDefault();
		this.osDragDepth.set(cat, (this.osDragDepth.get(cat) ?? 0) + 1);
		this.dragOverCategory.set(cat);
	}

	protected onOsDragOver(event: DragEvent, cat: FileCategory): void {
		if (!this.isUploadableCategory(cat) || !this.isOsFileDrag(event)) return;
		// Required every time dragover fires, not just on dragenter — the
		// browser only allows a drop on elements that keep calling
		// preventDefault() throughout the drag.
		event.preventDefault();
		// A media thumbnail is draggable="true" (reorder / timeline drag). Chromium
		// defaults such an element's dropEffect to "none" for an incoming OS file
		// drag, which silently rejects the drop (no `drop` event fires at all) even
		// though we preventDefault. Forcing "copy" makes a drop onto a thumbnail
		// behave like a drop onto the empty category area.
		if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
	}

	protected onOsDragLeave(event: DragEvent, cat: FileCategory): void {
		if (!this.isUploadableCategory(cat) || !this.isOsFileDrag(event)) return;
		const depth = (this.osDragDepth.get(cat) ?? 0) - 1;
		if (depth <= 0) {
			this.osDragDepth.delete(cat);
			if (this.dragOverCategory() === cat) this.dragOverCategory.set(null);
		} else {
			this.osDragDepth.set(cat, depth);
		}
	}

	protected onOsDrop(event: DragEvent, cat: FileCategory): void {
		if (!this.isUploadableCategory(cat) || !this.isOsFileDrag(event)) return;
		event.preventDefault();
		this.osDragDepth.delete(cat);
		this.dragOverCategory.set(null);

		const droppedFiles = Array.from(event.dataTransfer?.files ?? []);
		if (!this.projectSlug() || droppedFiles.length === 0) return;

		if (this.isCollapsed(cat)) this.toggleCategory(cat);

		const valid: File[] = [];
		const rejected: string[] = [];
		for (const file of droppedFiles) {
			const error = validateFileForCategory(file, cat);
			if (error) rejected.push(`${file.name}: ${error}`);
			else valid.push(file);
		}

		if (rejected.length > 0) {
			this.messages.add({
				severity: 'error',
				summary: rejected.length === 1 ? 'Invalid file' : `${rejected.length} invalid files`,
				detail: rejected.join('; '),
				life: 6000,
			});
		}

		if (valid.length > 0) this.processIncomingFiles(valid, cat);
	}

	/**
	 * A media thumbnail covers most of its category's drop area and is itself a
	 * drag source (`draggable="true"` for reorder / timeline drag). Native OS file
	 * drops that land on it don't reliably bubble to the category's onOs* handlers
	 * over a draggable child, so route them explicitly: OS file drags take the
	 * category upload path, everything else the reorder path. `stopPropagation` on
	 * the drop keeps the category's own onOsDrop from handling the same drop twice.
	 */
	protected onFileBoxDragOver(event: DragEvent, file: ProjectFile, cat: FileCategory): void {
		if (this.isOsFileDrag(event)) { this.onOsDragOver(event, cat); return; }
		if (this.canReorder()) this.onReorderDragOver(event, file);
	}

	protected onFileBoxDrop(event: DragEvent, file: ProjectFile, cat: FileCategory): void {
		if (this.isOsFileDrag(event)) {
			event.stopPropagation();
			this.onOsDrop(event, cat);
			return;
		}
		if (this.canReorder()) this.onReorderDrop(event, file, cat);
	}

	protected onDuplicateDialogVisibleChange(value: boolean): void {
		this.duplicateDialogVisible.set(value);
	}

	protected onDuplicateDialogConfirm(resolutions: DuplicateResolution[]): void {
		const slug = this.projectSlug();
		const category = this.duplicateDialogCategory;
		if (!slug || !category) {
			this.resetDuplicateDialog();
			return;
		}
		const resolutionMap = new Map<string, DuplicateResolutionEntry>();
		for (const r of resolutions) {
			resolutionMap.set(r.file.name, { action: r.action, replaceMediaId: r.replaceMediaId });
		}
		const allFiles: File[] = [
			...resolutions.map(r => r.file),
			...this.duplicateDialogNonDuplicates(),
		];
		this.uploadService.enqueue(allFiles, slug, category, { duplicateResolution: resolutionMap });
		this.resetDuplicateDialog();
	}

	protected onDuplicateDialogCancel(): void {
		this.resetDuplicateDialog();
	}

	private resetDuplicateDialog(): void {
		this.duplicateDialogVisible.set(false);
		this.duplicateDialogRows.set([]);
		this.duplicateDialogNonDuplicates.set([]);
		this.duplicateDialogCategory = null;
	}

	protected deleteVariantGroup(file: ProjectFile, event: MouseEvent): void {
		event.preventDefault();
		event.stopPropagation();
		const count = file.variants?.length ?? 0;
		if (!confirm(`Delete variant group "${file.name}" and all ${count} variant(s)? This removes all files from disk. Cannot be undone.`)) return;
		if (!file.id) return;
		this.performMediaDelete(file.id, file.name ?? 'this media', false);
	}

	protected deleteLocalFile(file: ProjectFile, event: MouseEvent): void {
		event.preventDefault();
		event.stopPropagation();
		const message = file.isLocal
			? `Delete "${file.name}"? This removes the file and all generated versions. Cannot be undone.`
			: `Delete "${file.name}" from the media pool? The remote CDN file stays — only the platform record is removed. Cannot be undone.`;
		if (!confirm(message)) return;
		if (!file.id) return;
		this.performMediaDelete(file.id, file.name ?? 'this media', false);
	}

	/**
	 * Delete a media, handling the "still referenced on the timeline" case: the
	 * backend answers the first (non-force) request with 409 + a references report;
	 * we warn the user exactly what will be removed, and on confirm re-issue the
	 * delete with force, which drops those references server-side (deletes
	 * referencing scenes, removes audio-track entries) so nothing is orphaned.
	 * When a force delete actually removed timeline references, emit timelineChanged
	 * so the editor reloads the project (the removed scenes/tracks are now gone).
	 */
	private performMediaDelete(id: string, name: string, force: boolean): void {
		this.filesService.deleteMedia(id, { force }).subscribe({
			next: (res) => {
				this.filesService.reloadCurrent();
				const cleaned = res?.cleaned;
				if (cleaned && (cleaned.scenesRemoved > 0 || cleaned.scenesEdited > 0 || cleaned.audioTracksRemoved > 0)) {
					this.timelineChanged.emit();
				}
				// Surgically clear this media from the open form (its src was scrubbed
				// server-side) without re-fetching the whole scene.
				this.mediaDeleted.emit(id);
			},
			error: (err: HttpErrorResponse) => {
				const body = (err?.error ?? {}) as { error?: string; references?: MediaReferenceReport };
				const refs = body.references;
				if (err?.status === 409 && refs) {
					// A scene is only DELETED when the media is its video background;
					// where it's just a mask/overlay the scene survives and only the
					// reference is stripped — so the warning must not threaten deletion.
					const scenesDeleted = refs.scenes?.filter(s => s.primaryBackground).length ?? 0;
					const scenesEdited = (refs.scenes?.length ?? 0) - scenesDeleted;
					const parts: string[] = [];
					if (refs.scenes?.length) parts.push(`${refs.scenes.length} scene${refs.scenes.length === 1 ? '' : 's'}`);
					if (refs.audioTracks) parts.push(`${refs.audioTracks} audio track${refs.audioTracks === 1 ? '' : 's'}`);
					const where = parts.join(' and ');
					const notes: string[] = [];
					if (scenesDeleted) notes.push(`${scenesDeleted} scene${scenesDeleted === 1 ? '' : 's'} will be removed from the timeline (it's their video background)`);
					if (scenesEdited) notes.push(`${scenesEdited} scene${scenesEdited === 1 ? '' : 's'} will keep everything, only this media's reference is cleared`);
					const sceneNote = notes.length ? ` ${notes.join('; ')}.` : '';
					if (confirm(`"${name}" is used on the timeline (${where}). Deleting it will also remove it from there.${sceneNote} Continue?`)) {
						this.performMediaDelete(id, name, true);
					}
					return;
				}
				alert(`Delete failed: ${String(body.error ?? err?.message ?? err)}`);
			},
		});
	}

	protected isLocalFile(file: ProjectFile): boolean {
		return !!file.isLocal;
	}

	/** A variant group with zero variants has nothing playable at its `baseUrl`
	 * yet — dragging it in would create a scene/audio ref whose `namesMap`
	 * entry is an empty list. Excluded from drag until at least one variant
	 * is uploaded. */
	protected isDraggableCategory(file: ProjectFile): boolean {
		if (file.variants && file.variants.length === 0) return false;
		return file.category === 'video' || file.category === 'audios' || file.category === 'images';
	}

	protected onDragStart(event: DragEvent, file: ProjectFile): void {
		if (!event.dataTransfer) return;
		// Only scenes (video/images) and sounds (audios) may be dropped into
		// the timeline — other categories are draggable purely for in-panel
		// reorder (see `canReorder()`), so they must not carry this payload:
		// the timeline's dragover only checks for its presence, not category,
		// so setting it unconditionally would highlight the timeline as a
		// valid drop target for e.g. fonts even though the drop itself is a
		// no-op.
		if (!this.isDraggableCategory(file)) return;
		event.dataTransfer.setData('application/webout-file', JSON.stringify({
			baseUrl: file.baseUrl,
			name: file.name,
			category: file.category,
			durationSeconds: file.durationSeconds,
			fps: file.fps,
		}));
		event.dataTransfer.effectAllowed = 'copy';
	}

	protected isUploadableCategory(cat: FileCategory): boolean {
		return cat === 'video' || cat === 'mask' || cat === 'images' || cat === 'audios' || cat === 'fonts' || cat === 'subtitles';
	}

	protected isVariantGroupCategory(cat: FileCategory): boolean {
		return cat === 'video' || cat === 'audios';
	}

	protected acceptForCategory = acceptForCategory;

	// ── Variant group operations ────────────────────────────────────────────

	protected creatingGroup = signal(false);

	protected createVariantGroup(cat: FileCategory): void {
		const name = prompt('Variant group name:');
		if (!name?.trim()) return;

		this.creatingGroup.set(true);
		this.filesService.createVariantGroup(cat, name.trim()).subscribe({
			next: (media) => {
				this.creatingGroup.set(false);
				this.filesService.reloadAndThen(() => this.openCreatedGroup(media._id, cat));
			},
			error: (err: ApiError) => {
				this.creatingGroup.set(false);
				alert(`Create group failed: ${String(err?.error?.error ?? err?.message ?? err)}`);
			},
		});
	}

	/** Opens the variant dialog for a just-created group once the file list
	 * has reloaded, and expands its category so the row is visible once the
	 * dialog is closed again. */
	private openCreatedGroup(mediaId: string, cat: FileCategory): void {
		const file = this.filesService.files().find(f => f.id === mediaId);
		if (!file) return;
		this.collapsed.update(set => {
			const next = new Set(set);
			next.delete(cat);
			return next;
		});
		this.variantFilter.set('');
		this.variantDialogFile.set(file);
		this.variantDialogVisible.set(true);
	}

	protected onVariantUploadSelected(event: Event): void {
		const input = event.target as HTMLInputElement;
		const files = input.files;
		const groupFile = this.variantDialogFile();
		const slug = this.projectSlug();
		if (!files?.length || !groupFile?.id || !slug) return;

		// Route variant uploads through UploadService so they surface in the
		// global upload panel as a single collapsed group row with per-file
		// progress (plan D8). Each file is probed individually (D10 throttles
		// to 3 concurrent probes) and sent as its own HTTP request.
		this.uploadService.enqueueVariantGroup(
			groupFile.id,
			groupFile.name,
			groupFile.category,
			files,
			slug,
		);
		input.value = '';
	}

	protected deleteVariant(variant: VariantFile, event: MouseEvent): void {
		event.preventDefault();
		event.stopPropagation();
		const groupFile = this.variantDialogFile();
		if (!groupFile?.id) return;
		if (!confirm(`Delete variant "${variant.name}"?`)) return;

		const groupId = groupFile.id;
		this.filesService.deleteVariant(groupId, variant.encodedName).subscribe({
			next: () => {
				const slug = this.projectSlug();
				if (slug) this.filesService.reloadAndThen(() => this.variantGroupChanged.emit(groupId));
				// Update dialog — remove the deleted variant locally for immediate feedback
				const current = this.variantDialogFile();
				if (current?.variants) {
					const updated = { ...current, variants: current.variants.filter(v => v.encodedName !== variant.encodedName) };
					this.variantDialogFile.set(updated);
				}
			},
			error: (err: ApiError) => {
				alert(`Delete variant failed: ${String(err?.error?.error ?? err?.message ?? err)}`);
			},
		});
	}

	protected renameGroup(newName: string): void {
		const file = this.variantDialogFile();
		if (!file?.id || !newName.trim() || newName.trim() === file.name) return;
		const trimmed = newName.trim();
		// Update dialog title immediately
		this.variantDialogFile.set({ ...file, name: trimmed });
		this.filesService.renameMedia(file.id, trimmed).subscribe({
			next: () => {
				const slug = this.projectSlug();
				if (slug) this.filesService.reloadCurrent();
			},
			error: (err: ApiError) => {
				alert(`Rename failed: ${String(err?.error?.error ?? err?.message ?? err)}`);
			},
		});
	}

}
